import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TOAST_LIST_ID, ToastService } from '../../../services/toast/toast.service';
import { ThemeService } from '../../../services/theme.service';

/**
 * Renders ToastService's toasts. ToastService adds one to <body> on first
 * use; placing <bk-toast-outlet /> in a template instead works too. Outside
 * the app's `.dark` wrapper it carries the theme class itself, like the
 * floating menus do.
 */
@Component({
  selector: 'bk-toast-outlet',
  standalone: true,
  templateUrl: './toast-outlet.component.html',
  styleUrl: './toast-outlet.component.css',
  host: {
    '[class.dark]': 'isDark',
  },
})
export class ToastOutletComponent implements OnInit {
  readonly service = inject(ToastService);
  readonly listId = TOAST_LIST_ID;
  private readonly theme = inject(ThemeService);

  /**
   * The live region has to be in the page before anything is written into
   * it, or screen readers skip the first toast. Content shows a beat later.
   */
  private readonly ready = signal(false);
  readonly visible = computed(() => (this.ready() ? this.service.toasts() : []));

  constructor() {
    this.service.registerOutlet();
  }

  get isDark(): boolean {
    return this.theme.currentTheme === 'dark';
  }

  /**
   * Hover pauses a toast for mouse users only. A tap also fires pointerenter,
   * and pausing then would leave the toast up until the next tap elsewhere.
   */
  onPointerEnter(event: PointerEvent, id: number): void {
    if (event.pointerType === 'mouse') this.service.pause(id);
  }

  onPointerLeave(event: PointerEvent, id: number): void {
    if (event.pointerType === 'mouse') this.service.resume(id);
  }

  ngOnInit(): void {
    if (typeof window === 'undefined') return;
    setTimeout(() => this.ready.set(true), 60);
  }
}
